import { useEffect, useState } from 'react';
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { X } from 'lucide-react';

// FD-284 — the mapping freeze, stated UP FRONT.
//
// The freeze begins when a campaign's wave is activated: the BPRT is minted at
// that moment, so execution has demonstrably begun and the source→destination
// mapping a flight plan was built from must not move underneath it.
//
// FD-380 (FD-ADR-036, FD-ADR-037 §4) — THE FREEZE IS ADDITIVE-ONLY, and this
// banner used to say otherwise. FD-ADR-025 D4's wholesale immutability was
// relaxed because it was self-defeating: a device held at
// `409 IDENTITY_NOT_READY` is released by ADDING the mapping it is waiting for,
// and under the old rule that add could never be written while the wave ran —
// in exactly the scenario the gate exists for. So one predicate now carries
// three behaviours, and stating them is the whole point of the banner:
//
//   • ADD a mapping that does not exist  → allowed, audited as
//     `identity-mapping.added-late`
//   • UPDATE an existing mapping         → per-row reject inside the `rejected`
//     bucket (the batch comes back 207)
//   • REMOVE any mapping                 → whole request `409 MAPPINGS_FROZEN`
//
// The safety property is unchanged: a MUTATED mapping genuinely redirects a
// running device, because devices re-fetch their plan on resume (FA-ADR-021);
// an ADDED row can only reach a device still holding at the gate, which has
// acted on no identity yet.
//
// Until now nothing in the console said so. The freeze was discoverable only by
// editing a mapping and being rejected — which is the worst possible moment to
// learn it, because the operator has already done the work. `bprtStatus` has
// been on the migration record since FD-156 and was simply never read.
//
// The predicate is exactly the server's (`Test-MappingsFrozen`): frozen iff
// `BprtStatus == active`. It is deliberately NOT "the wave has ever been
// activated" — a completed wave has had its BPRT revoked, and its mappings are
// no longer frozen by this rule.

/** Frozen iff the wave BPRT is active — the same predicate as `Test-MappingsFrozen`. */
export function areMappingsFrozen(bprtStatus: string | null | undefined): boolean {
  return (bprtStatus ?? '').toLowerCase() === 'active';
}

// FD-493 — the freeze is now stated in TWO places, and the copy is exported so
// there is still only ONE wording of it.
//
// The `/activate` preflight route has to tell an operator what activation does
// BEFORE they press the button, and the mapping half of that answer is the
// sentence below — already written, already reviewed, already tested. The
// mockups' own version of the same paragraph says devices "can no longer be
// added, removed, or moved", which is false three ways (R-FD-051 D1), so the
// risk here is not a missing sentence: it is a SECOND sentence, written fresh on
// a page whose whole job is to be truthful about activation, drifting from this
// one.
//
// Exported as constants rather than by mounting this component there, because
// this component renders NOTHING until `bprtStatus === 'active'` — which is
// exactly the state the preflight page exists to precede.

/** The banner's own title. Only true once the wave IS active. */
export const MAPPINGS_FROZEN_TITLE =
  'This wave is active — mappings can be added, not changed';

/**
 * The clause that states the rule itself, tense-free, so it reads correctly both
 * as a live banner and as a description of what activation will do.
 */
export const MAPPINGS_FROZEN_RULE = 'mappings can be added, not changed';

/** The body. Verbatim in both surfaces — see the note above. */
export const MAPPINGS_FROZEN_DETAIL =
  'Existing mappings are locked because the flight plans already issued were built ' +
  'from them. You can still add a mapping for an identity that has none — that is how ' +
  'a device waiting on its identity is released. Changes and removals are rejected by ' +
  'the server, not just hidden here.';

export interface MappingsFrozenBannerProps {
  bprtStatus: string | null | undefined;
}

export default function MappingsFrozenBanner({ bprtStatus }: MappingsFrozenBannerProps) {
  const frozen = areMappingsFrozen(bprtStatus);
  const [dismissed, setDismissed] = useState(false);

  // Re-arm when the wave stops being active and later becomes active again — a
  // supplementary wave (FD-ADR-035 §3) is a NEW freeze, and an operator who
  // dismissed the last one has not been told about this one. Keyed on the
  // predicate rather than on the raw status so `revoked` → `active` and
  // `expired` → `active` both re-arm without enumerating the vocabulary here.
  useEffect(() => {
    if (!frozen) setDismissed(false);
  }, [frozen]);

  if (!frozen || dismissed) return null;

  return (
    // FD-501 — FULL WIDTH of the content column. It carried `max-w-[70ch]`, a
    // reading measure borrowed from body prose, which left it ending mid-column
    // above a full-width table and reading as an unfinished element rather than
    // as the page-level notice it is. A MessageBar is chrome for the surface
    // below it; the surface below it is the table.
    <div className="mb-4">
      {/* `warning`, not `error`: nothing has failed. A frozen campaign is a
          campaign that is running, which is the normal and desired state. */}
      <MessageBar intent="warning">
        <MessageBarBody>
          <MessageBarTitle>{MAPPINGS_FROZEN_TITLE}</MessageBarTitle>
          {MAPPINGS_FROZEN_DETAIL}
        </MessageBarBody>
        {/* Dismissible, and the state is LOCAL rather than in the URL or in
            `localStorage`. The ADR-015 rule keeps VIEW state in the URL so a
            filtered page can be shared; whether one operator has read one
            notice is not view state, and a shared link that arrives with the
            freeze notice already hidden would be worse than one that repeats
            it. `localStorage` is never considered (FD-ADR-043, ADR-015).

            What dismissing does NOT do is change what the server allows: the
            affordances the freeze governs are disabled by
            `IdentityMappingsSection` from `areMappingsFrozen` independently of
            this banner, and removal is still a whole-request
            `409 MAPPINGS_FROZEN` (FD-ADR-036, FD-ADR-037 §4). Hiding the
            explanation cannot unhide the action. */}
        <MessageBarActions
          containerAction={
            <Button
              appearance="transparent"
              aria-label="Dismiss the mapping freeze notice"
              icon={<X size={16} strokeWidth={1.5} />}
              onClick={() => setDismissed(true)}
            />
          }
        />
      </MessageBar>
    </div>
  );
}
